import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { formatCurrency } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { CalendarDays, Coins, AlertCircle } from "lucide-react";
import { useMemo } from "react";

export function RenewalForecast() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["/api/dashboard/stats"],
  });

  const weeklyForecast = useMemo(() => {
    if (!stats) return null;
    return {
      count: stats.weekRenewals,
      cost: stats.weekRenewalsCost,
    };
  }, [stats]);

  const todayForecast = useMemo(() => {
    if (!stats) return null;
    return {
      count: stats.todayRenewals,
      cost: stats.todayRenewalsCost,
    };
  }, [stats]);

  if (statsLoading) {
    return <RenewalForecastSkeleton />;
  }

  if (!stats) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Coins className="h-5 w-5 text-muted-foreground" />
            Cost Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-1">Unable to load forecast data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <Coins className="h-5 w-5 text-muted-foreground" />
          Cost Forecast
        </CardTitle>
        <CardDescription>Upcoming subscription renewals</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-sm font-medium">Today</h4>
              <div className="flex items-center mt-0.5">
                <CalendarDays className="h-4 w-4 text-muted-foreground mr-1" />
                <span className="text-sm text-muted-foreground">
                  {todayForecast?.count === 0 ? "No renewals today" : `${todayForecast?.count} license${todayForecast?.count === 1 ? "" : "s"}`}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-semibold">
                {todayForecast?.count === 0 ? "-" : formatCurrency(todayForecast?.cost || 0)}
              </span>
              {todayForecast?.count > 0 && (
                <div className="text-xs text-muted-foreground">Due today</div>
              )}
            </div>
          </div>
          
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-sm font-medium">Next 7 days</h4>
              <div className="flex items-center mt-0.5">
                <CalendarDays className="h-4 w-4 text-muted-foreground mr-1" />
                <span className="text-sm text-muted-foreground">
                  {weeklyForecast?.count === 0 ? "No renewals this week" : `${weeklyForecast?.count} license${weeklyForecast?.count === 1 ? "" : "s"}`}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-semibold">
                {weeklyForecast?.count === 0 ? "-" : formatCurrency(weeklyForecast?.cost || 0)}
              </span>
              {weeklyForecast?.count > 0 && (
                <div className="text-xs text-muted-foreground">Due this week</div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-1">
        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link href="/renewals">
            View All Renewals
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function RenewalForecastSkeleton() {
  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-medium">
          <Skeleton className="h-6 w-36" />
        </CardTitle>
        <CardDescription>
          <Skeleton className="h-4 w-52" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <Skeleton className="h-5 w-24 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="text-right">
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-3 w-16 ml-auto" />
            </div>
          </div>
          
          <div className="flex justify-between items-start">
            <div>
              <Skeleton className="h-5 w-28 mb-2" />
              <Skeleton className="h-4 w-36" />
            </div>
            <div className="text-right">
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-3 w-20 ml-auto" />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-1">
        <Skeleton className="h-9 w-full" />
      </CardFooter>
    </Card>
  );
}